import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Button, Input, Typography, message } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { getFileIcon } from "../../../helpers/getFileIcon";
import GridPdfViewer from "../InvestigationsIp/InvestigationGallery/GridPdfViewer";
import {
  validateGalleryFileName,
  getGalleryFileNameEditError,
} from "../../../helpers/galleryUtils";
import styles from "./uploadFilePreview.module.css";

const getDisplayName = (item) =>
  item?.file_name ?? item?.path?.split("/").pop() ?? "";

const isImageType = (type) => type?.startsWith("image/");
const isPdfType = (type) => type === "application/pdf";

const UploadFilePreviewList = ({
  uploadFile,
  onRemove,
  onFileNameChange,
  isMobile = false,
}) => {
  const [editingPath, setEditingPath] = useState(null);
  const [editName, setEditName] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editingPath) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [editingPath]);

  const handleEditStart = (item) => {
    setEditingPath(item.path);
    setEditName(getDisplayName(item));
  };

  const handleEditConfirm = (item) => {
    const trimmed = editName.trim();
    const validationError = validateGalleryFileName(trimmed);
    if (validationError) {
      message.error(validationError);
      return;
    }

    onFileNameChange(item.path, trimmed);
    setEditingPath(null);
    setEditName("");
  };

  const handleEditCancel = () => {
    setEditingPath(null);
    setEditName("");
  };

  const handleRemove = (item) => {
    if (editingPath === item.path) {
      setEditingPath(null);
      setEditName("");
    }
    onRemove(item);
  };

  return (
    <Row
      gutter={isMobile ? [8, 12] : [16, 16]}
      className={styles.uploadFileList}
      wrap
    >
      {uploadFile.map((item, index) => {
        const displayName = getDisplayName(item);
        const isEditing = editingPath === item.path;

        return (
          <Col
            className="inv-upload-file"
            span={24}
            xs={24}
            key={item.path || index}
          >
            <div className={styles.uploadFileCard}>
              <div className={styles.uploadPreviewBox}>
                {isImageType(item.type) && item.preview_url ? (
                  <img
                    src={item.preview_url}
                    alt={displayName}
                    className={styles.uploadPreviewImage}
                  />
                ) : isPdfType(item.type) && item.preview_url ? (
                  <GridPdfViewer
                    className="img-card"
                    data={[
                      {
                        url: item.preview_url,
                        type: item.type,
                        file_name: displayName,
                      },
                    ]}
                    pageNumber={1}
                    height="140px"
                    width="100%"
                    fill
                  />
                ) : (
                  <div className={styles.uploadPreviewIcon}>{getFileIcon(item)}</div>
                )}
              </div>

              <div className={styles.uploadFileHeader}>
                <div className={styles.uploadFileActions}>
                  {!isEditing ? (
                    <>
                      <Button
                        type="text"
                        className={styles.actionBtn}
                        icon={<EditOutlined />}
                        onClick={() => handleEditStart(item)}
                        aria-label="Edit file name"
                      />
                      <Button
                        type="text"
                        className={`${styles.actionBtn} ${styles.deleteBtn}`}
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemove(item)}
                        aria-label="Remove file"
                      />
                    </>
                  ) : (
                    <Button
                      type="text"
                      className={`${styles.actionBtn} ${styles.deleteBtn}`}
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemove(item)}
                      aria-label="Remove file"
                    />
                  )}
                </div>
              </div>

              {isEditing ? (
                <div className={styles.editBlock}>
                  <div className={styles.editInputRow}>
                    <Input
                      ref={inputRef}
                      className={styles.fileNameInput}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onPressEnter={() => handleEditConfirm(item)}
                      placeholder="Enter file name"
                      status={getGalleryFileNameEditError(editName) ? "error" : undefined}
                    />
                    <CheckOutlined
                      className={styles.confirmIcon}
                      onClick={() => handleEditConfirm(item)}
                      aria-label="Save file name"
                    />
                    <CloseOutlined
                      className={styles.cancelIcon}
                      onClick={handleEditCancel}
                      aria-label="Cancel edit"
                    />
                  </div>
                  {getGalleryFileNameEditError(editName) && (
                    <Typography.Text type="danger" className={styles.editError}>
                      {getGalleryFileNameEditError(editName)}
                    </Typography.Text>
                  )}
                </div>
              ) : (
                <Typography.Text
                  title={displayName}
                  className={styles.fileName}
                >
                  {displayName}
                </Typography.Text>
              )}
            </div>
          </Col>
        );
      })}
    </Row>
  );
};

export default UploadFilePreviewList;
